/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  КОНТРАКТНАЯ ПОВЕРХНОСТЬ API — ФАЗА 2 (выведена из `api.gen.ts`)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Единственный модуль, через который приложение видит бэкенд. Всё остальное
 * (`lib/`, `api/`, `stores/`, `views/`, `mocks/`) импортирует типы **только отсюда**.
 *
 * `src/types/api.gen.ts` генерируется из живого контракта командой
 *
 *     npm run api:types      # openapi-typescript ../backend/openapi.yaml -o src/types/api.gen.ts
 *
 * и **коммитится** — фронт обязан собираться без чекаута backend.
 * Генератор запущен с `fq_schema_naming=true`, поэтому ключи там выглядят как
 * `components['schemas']['scanner.admin.v1.User']`.
 *
 * ⚠️ ПРАВИЛО, КОТОРОЕ ДЕЛАЕТ ЭТУ ПРОСЛОЙКУ ОСМЫСЛЕННОЙ:
 *    **никто за пределами `src/types/` не имеет права импортировать `api.gen.ts`.**
 *    Сгенерированные имена уродливы, нестабильны между версиями gnostic и
 *    ломаются при любом переименовании в `.proto`. Прослойка стоит одного файла.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ЧТО ИМЕННО ПРИХОДИТСЯ ПРАВИТЬ ПОВЕРХ ГЕНЕРАТОРА (и почему)
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Цепочка `protoc-gen-openapi (gnostic) → openapi-typescript` теряет три вещи.
 * Все три чинятся здесь механически, а не переписыванием типов руками:
 *
 * 1. **`Required<...>` — потому что в спеке нет `required`.** gnostic не эмитит
 *    список `required` ни для одной схемы, а `openapi-typescript` честно делает
 *    из этого `id?: string` на КАЖДОМ поле. Сырой `User` со всеми полями
 *    `string | undefined` отравил бы каждый вызов: `u.email` перестал бы быть
 *    строкой, а `u.role` — сравнимым с `'ROLE_ADMIN'`. При этом по проводу
 *    (protojson, `EmitUnpopulated: true`) присутствует **каждое** поле — см.
 *    раздел «Формат провода» ниже. Поэтому `Wire<K> = Required<Schemas[K]>`.
 *    Ровно два места, где `?` подлинный (proto3 `optional` в `UpdateUserRequest`
 *    и query-параметры `ListUsers`), `Required` не применяется.
 *
 * 2. **`| null` на message-полях — OpenAPI 3.0 его здесь не выражает.**
 *    `User.lastLoginAt` и `ListUsersResponse.pageInfo` — вложенные сообщения,
 *    и protojson кодирует «не выставлено» как `null`, а не как отсутствие ключа.
 *    В сгенерированном типе они `string` / `PageInfo`. Оба перекрыты ниже
 *    точечно, через `Omit<..., 'k'> & { k: T | null }`.
 *
 * 3. **Path-параметры протекают в тело запроса.** gnostic кладёт `id` в схему
 *    `UpdateUserRequest` / `SetUserRoleRequest` / `ResetUserPasswordRequest`,
 *    хотя `id` едет в URL и телом не перекрывается. Снимается `Omit<..., 'id'>`,
 *    иначе вызывающий код мог бы положить `id` в тело и молча ничего не сделать.
 *
 * ЧТО ОСТАЛОСЬ РУКОПИСНЫМ и почему (список закрытый):
 *
 *   • `ErrorResponse`, `ERROR_REASONS`, `ErrorReason`, `UNNORMALIZED_ERROR_REASONS`
 *     — **в `openapi.yaml` их нет физически.** gnostic эмитит только схемы,
 *       достижимые из RPC, а конверт ошибки кратоса не является ответом ни одного
 *       RPC. Источник истины — `api/admin/v1/error_reason.proto`.
 *   • `PERMISSIONS` / `Permission` — в контракте это `permissions: string[]`;
 *     union нужен фронту для словарей, но авторитет всё равно сервер.
 *   • `WEAK_PASSWORD_RULES` / `WeakPasswordRule` — значения `metadata.rule`,
 *     то есть содержимое `map<string,string>`; в схеме их не видно в принципе.
 *   • `USER_ORDER_BY` / `UserOrderBy` — в спеке `orderBy?: string`. Рукописный
 *     закрытый union **строже** сгенерированного и ловит опечатку компиляцией,
 *     а список всё равно закрыт на сервере.
 *   • `ROLE_LABELS`, `STATUS_LABELS`, `ASSIGNABLE_*`, `EmptyMessage` — это UI и
 *     эргономика, а не контракт.
 *
 * Полнота enum'ов проверяется бесплатно и в обе стороны: `ROLES`/`USER_STATUSES`
 * объявлены через `satisfies readonly Role[]` (лишнее значение → ошибка), а
 * `ROLE_LABELS`/`STATUS_LABELS` — через тотальный `Record<Role, string>`
 * (недостающее значение → ошибка). Новый вариант enum'а в `.proto` ломает
 * **компиляцию**, а не рантайм.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ФОРМАТ ПРОВОДА — go-kratos v2.9.2, кодек `protojson`
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Кратос сериализует ответы через `protojson` с `EmitUnpopulated: true` и
 * **невыставленным** `UseProtoNames` (то есть `false`). Отсюда пять следствий,
 * и все пять уже учтены в типах ниже — не «поправляйте» их по виду `.proto`:
 *
 * 1. **Ключи — lowerCamelCase.** В `.proto` написано `full_name`, по проводу
 *    приходит `fullName`. Это интеграционная мина №1; в `.proto` не подсматриваем.
 *
 * 2. **Присутствует КАЖДОЕ поле в КАЖДОМ ответе.** `EmitUnpopulated` означает,
 *    что ключ есть всегда, а «пусто» кодируется значением:
 *      • скаляр  (string/number/bool) → нулевое значение: `""`, `0`, `false`;
 *      • **message-поле** (включая `Timestamp`) → **`null`**, ключ присутствует
 *        со значением `null` ⇒ тип `T | null`, **не** `T?`;
 *      • `repeated` → `[]` (никогда не `null` и никогда не отсутствует);
 *      • `map` → `{}`.
 *    Практический вывод: `resp.users.length` и `err.metadata[k]` читаются
 *    безусловно, без `?.` и без `?? []`.
 *
 * 3. **Исключение — proto3 `optional`.** Такие поля компилируются в синтетический
 *    `oneof`, и `EmitUnpopulated` на них не распространяется: невыставленное
 *    поле **отсутствует в JSON целиком**. Только их и только их типизируем `T?`.
 *    В этом контракте это ровно четыре поля `UpdateUserRequest`.
 *
 * 4. **Enum'ы едут строками**: `"ROLE_ADMIN"`, `"USER_STATUS_ACTIVE"` — не числами.
 *    Нулевое значение (`ROLE_UNSPECIFIED`) тоже приезжает именем.
 *
 * 5. **Timestamp — RFC3339 в UTC с литеральной `Z`**, дробная часть печатается
 *    только когда не нулевая: `2026-08-04T11:30:12.340Z` и `2026-08-04T09:15:00Z`
 *    — обе формы валидны и обе встречаются в одном ответе.
 *    ⇒ Разбирать **только** `new Date(iso)`. Резать по фиксированным позициям
 *    (`iso.slice(0, 19)`) нельзя — длина строки плавает.
 *
 * 6. **Пагинация объявлена `uint32`** ровно затем, чтобы приезжать JSON-числами.
 *    (`int64`/`uint64` protojson отдаёт строками — здесь этого нет.)
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ⚠️ ЖИВАЯ МИНА ТРАНСПОРТА: `Content-Type` обязателен даже на пустом теле
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Кратос требует `Content-Type: application/json` на **каждом** запросе, у
 * которого в `google.api.http` объявлен `body` — включая те, чьё тело пустое.
 *
 *     axios.post('/api/v1/auth/sessions/revoke-others')        // ← 400
 *     axios.post('/api/v1/auth/sessions/revoke-others', {})    // ← 200 OK
 *
 * Без второго аргумента axios не проставляет заголовок вообще, декодер кратоса
 * не находит кодек и отвечает `400`. **Всегда передавайте тело — хотя бы `{}`.**
 *
 * Проверено curl'ом на живом бэкенде (F4): ответ на POST без `Content-Type` —
 *
 *     {"code":400,"reason":"VALIDATION_FAILED",
 *      "message":"unregister Content-Type: ","metadata":{}}
 *
 * то есть `reason` не `"CODEC"`, как можно было ожидать: кратос заворачивает
 * ошибку декодера в свой `VALIDATION_FAILED`. Для фронта это значит, что
 * пропущенный `Content-Type` неотличим от настоящей ошибки валидации — ещё одна
 * причина не полагаться на диагностику, а всегда слать тело. Страховка стоит в
 * `lib/http.ts`: request-интерцептор подставляет `config.data ??= {}` для
 * `post`/`put`/`patch`, поэтому забыть второй аргумент физически безопасно.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  Прочее, что нужно знать вызывающему коду
 * ───────────────────────────────────────────────────────────────────────────
 *
 * • Авторизация — `Authorization: Bearer <accessToken>`.
 * • `baseURL` пустой: пути в `google.api.http` уже начинаются с `/api/v1`,
 *   Vite-прокси ничего не переписывает (§2.2 плана).
 * • Идентификаторы в путях (`{id}`, `{sessionId}`) в тело **не** попадают —
 *   поэтому в `*Request` ниже их нет.
 * • `POST /api/v1/auth/refresh` обязан быть single-flight: сервер ротирует
 *   refresh-токен на каждое использование (§2.5 плана).
 */

import type { components, operations } from '@/types/api.gen'

/* ═════════════════════════════════════════════════════════════════════════
   0. МОСТ К ГЕНЕРАТОРУ
   ═════════════════════════════════════════════════════════════════════════ */

type Schemas = components['schemas']

/**
 * Схема сообщения с восстановленной обязательностью полей.
 *
 * `protoc-gen-openapi` не эмитит `required`, поэтому `openapi-typescript`
 * помечает `?` вообще всё. По проводу же присутствует каждое поле
 * (`EmitUnpopulated: true`), так что `Required<>` — это не ужесточение
 * контракта, а восстановление того, что потерялось в OpenAPI-представлении.
 * Там, где `?` подлинный (proto3 `optional`), `Wire` не применяется.
 */
type Wire<K extends keyof Schemas> = Required<Schemas[K]>

/**
 * Схема сообщения с точечно перекрытыми полями.
 *
 * Нужна по двум поводам, и оба — ограничения представления, а не контракта:
 *   • `Required<>` снимает `?` только на верхнем уровне, поэтому вложенное
 *     сообщение остаётся сырым (`tokens?.accessToken?: string`) и подменяется
 *     нашим уже исправленным типом;
 *   • `null` от protojson в OpenAPI 3.0 не выражается (`lastLoginAt`, `pageInfo`).
 */
type WireWith<K extends keyof Schemas, Override> = Omit<Wire<K>, keyof Override> & Override

/** Схема тела запроса без path-параметра, который gnostic затащил внутрь. */
type WireBody<K extends keyof Schemas> = Required<Omit<Schemas[K], 'id'>>

/* ═════════════════════════════════════════════════════════════════════════
   1. ENUM'Ы И ПОДПИСИ
   ═════════════════════════════════════════════════════════════════════════
   `enum` в TS запрещён (`erasableSyntaxOnly: true` в tsconfig.app.json), да и
   не нужен: `as const`-массив даёт и union для типов, и итерируемый список
   для `<Select>`, и рантайм-проверку `includes` — одним объявлением. */

/**
 * Значения enum'а `scanner.admin.v1.Role` ровно как они едут по проводу.
 * Union берётся из контракта (`enum_type=string` в `--openapi_out`), список
 * `ROLES` остаётся рукописным — итерируемого рантайм-значения из типа не сделать.
 * `satisfies` ловит лишнее значение, `ROLE_LABELS` ниже — недостающее.
 */
export type Role = NonNullable<Schemas['scanner.admin.v1.User']['role']>
export const ROLES = ['ROLE_UNSPECIFIED', 'ROLE_ADMIN', 'ROLE_VIEWER'] as const satisfies readonly Role[]

/** Значения enum'а `scanner.admin.v1.UserStatus` ровно как они едут по проводу. @see Role */
export type UserStatus = NonNullable<Schemas['scanner.admin.v1.User']['status']>
export const USER_STATUSES = [
  'USER_STATUS_UNSPECIFIED',
  'USER_STATUS_ACTIVE',
  'USER_STATUS_DISABLED',
] as const satisfies readonly UserStatus[]

/**
 * Роли, которые реально можно назначить: `*_UNSPECIFIED` — это протобуфный ноль,
 * а не выбор пользователя. Для `<Select>` в формах создания и смены роли.
 */
export const ASSIGNABLE_ROLES = ['ROLE_ADMIN', 'ROLE_VIEWER'] as const satisfies readonly Role[]

/** Статусы, которые реально можно выставить через `PATCH /users/{id}`. */
export const ASSIGNABLE_USER_STATUSES = ['USER_STATUS_ACTIVE', 'USER_STATUS_DISABLED'] as const satisfies readonly UserStatus[]

/**
 * Русские подписи ролей — **единственное** место, где они живут.
 * Карты тотальные (`Record<Role, string>`), поэтому индексация всегда даёт
 * строку и `?? fallback` по вьюхам не расползается.
 *
 * `ROLE_UNSPECIFIED` на живом пользователе появиться не должен, но в union он
 * есть (protojson печатает нулевое значение именем), поэтому подпись у него тоже есть.
 */
export const ROLE_LABELS: Record<Role, string> = {
  ROLE_UNSPECIFIED: 'Не указана',
  ROLE_ADMIN: 'Администратор',
  ROLE_VIEWER: 'Наблюдатель',
}

/** Русские подписи статусов. См. комментарий к `ROLE_LABELS`. */
export const STATUS_LABELS: Record<UserStatus, string> = {
  USER_STATUS_UNSPECIFIED: 'Не указан',
  USER_STATUS_ACTIVE: 'Активен',
  USER_STATUS_DISABLED: 'Отключён',
}

/* ═════════════════════════════════════════════════════════════════════════
   2. БАЗОВЫЕ СООБЩЕНИЯ
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * `scanner.admin.v1.User`.
 *
 * • `fullName` — `""`, когда не заполнено. Это скаляр, `null` в нём не бывает.
 * • `lastLoginAt` — `Timestamp`, то есть message-поле ⇒ `null` у того, кто
 *   ещё ни разу не входил. Единственное nullable-поле пользователя.
 * • `createdAt` / `updatedAt` сервер выставляет всегда.
 * • `id` — UUIDv4 (§1.3 плана), торчит в URL как `/users/{id}`.
 *
 * ⚠️ `lastLoginAt` перекрыт вручную: в спеке это `string` (`format: date-time`),
 * OpenAPI 3.0 не выражает protojson'овский `null` для невыставленного
 * message-поля. Проверено curl'ом на живом бэкенде — только что созданный
 * пользователь приезжает с `"lastLoginAt": null`.
 */
export type User = WireWith<'scanner.admin.v1.User', { lastLoginAt: string | null }>

/**
 * `scanner.admin.v1.Session` — одна строка = одно устройство/логин.
 * Хеш токена ротируется в этой же строке, поэтому список сессий = список
 * устройств, а не поколений токенов (§1.3 плана).
 *
 * • `deviceLabel` — уже разобранный на сервере User-Agent («Chrome на macOS»).
 *   Приоритетнее клиентского `parseUserAgent()` из `lib/format.ts`; тот — фолбэк
 *   на случай пустой строки.
 * • `expiresAt` — **абсолютный** срок от момента логина, при ротации не двигается.
 * • `isCurrent` сервер считает сам, сравнивая с `sid` из access-токена.
 * • `rememberMe` фиксируется при логине и при ротации неизменен.
 */
export type Session = Wire<'scanner.admin.v1.Session'>

/**
 * `scanner.admin.v1.PageInfo`. Все четыре поля — `uint32`, приезжают числами.
 * `page` — единичная база (первая страница = `1`).
 */
export type PageInfo = Wire<'scanner.admin.v1.PageInfo'>

/**
 * `scanner.admin.v1.TokenPair`.
 *
 * • `expiresIn` — время жизни **access**-токена в секундах (по конфигу — `900`).
 * • `refreshExpiresAt` — RFC3339-момент смерти refresh-токена; абсолютный,
 *   при ротации не продлевается.
 * • `tokenType` — `"Bearer"`.
 */
export type TokenPair = Wire<'scanner.admin.v1.TokenPair'>

/**
 * Ответ на RPC с пустым сообщением: по проводу — буквально `{}`.
 * В роли запроса напоминает, что тело всё равно нужно отправить (см. мину
 * с `Content-Type` в шапке файла).
 */
export type EmptyMessage = Record<string, never>

/* ═════════════════════════════════════════════════════════════════════════
   3. AUTH — `AuthService`, префикс `/api/v1/auth`
   ═════════════════════════════════════════════════════════════════════════

   Замечание о nullability во всех `*Response` этого раздела: `tokens`, `user`,
   `session` — message-поля, и формально protojson мог бы отдать в них `null`.
   Здесь они типизированы **не**-nullable сознательно: сервер заполняет их на
   каждом успешном ответе, а `null` там означал бы баг бэкенда, а не штатное
   состояние. Из полей верхнего уровня штатно бывает `null` только `pageInfo`
   в `ListUsersResponse`, и оно так и типизировано.

   Отдельно про вложенные поля: `User.lastLoginAt` штатно `null` у того, кто ещё
   ни разу не входил. Это то самое место, где `new Date(u.lastLoginAt)` молча
   даёт 1970 год — форматтеры в `lib/format.ts` возвращают на `null` прочерк. */

/**
 * `POST /api/v1/auth/login` — публичный.
 * `login` принимает **email или username** одним полем; сервер сам разбирается.
 * `rememberMe` определяет и срок refresh-токена на сервере (12 ч / 30 дней),
 * и хранилище на клиенте (`sessionStorage` / `localStorage`, §2.5 плана).
 */
export type LoginRequest = Wire<'scanner.admin.v1.LoginRequest'>

/**
 * Ответ логина. `session` — своя же сессия, чтобы фронт сразу знал свой `sessionId`.
 *
 * ⚠️ РУКОПИСНЫЙ — и это осознанно. Все три поля здесь вложенные сообщения,
 * каждое из которых пришлось бы перекрыть (`WireWith<..., {tokens, user, session}>`),
 * после чего от сгенерированной схемы не осталось бы ни одного собственного поля:
 * `Omit<Wire<'LoginResponse'>, 'tokens'|'user'|'session'>` — это `{}`. То есть
 * запись через генератор была бы этим же объявлением плюс шум, и дрейф ловила бы
 * ровно так же (никак). Это правило действует на все чистые обёртки ниже.
 */
export interface LoginResponse {
  tokens: TokenPair
  user: User
  session: Session
}

/**
 * `POST /api/v1/auth/refresh` — публичный.
 *
 * ⚠️ Вызов обязан быть single-flight: сервер ротирует refresh на каждое
 * использование, два параллельных вызова гонятся, и второй предъявит уже
 * погашенный токен. Серверное окно 30 с — страховка, а не разрешение фанаутить.
 */
export type RefreshRequest = Wire<'scanner.admin.v1.RefreshRequest'>

/**
 * Ответ refresh: новая пара токенов + перечитанный пользователь (роль могли сменить).
 * Рукописный — чистая обёртка, см. `LoginResponse`.
 */
export interface RefreshResponse {
  tokens: TokenPair
  user: User
}

/**
 * `POST /api/v1/auth/logout` — публичный и идемпотентный:
 * обязан отрабатывать даже с протухшим access-токеном.
 */
export type LogoutRequest = Wire<'scanner.admin.v1.LogoutRequest'>

/** `{}` — gnostic эмитит пустое сообщение ровно как `Record<string, never>`. */
export type LogoutResponse = Schemas['scanner.admin.v1.LogoutResponse']

/**
 * `GET /api/v1/auth/me` — любая роль. Тела запроса нет.
 *
 * `permissions` типизирован `string[]`, а не `Permission[]`, намеренно:
 * авторитет — сервер, и он вправе прислать право, о котором этот билд фронта
 * не знает. Известные значения — в `PERMISSIONS`.
 */
export type GetMeResponse = WireWith<'scanner.admin.v1.GetMeResponse', { user: User }>

/**
 * `POST /api/v1/auth/me/password` — любая роль, только свой пароль.
 * Защищён тем же rate-limit'ом, что и логин: проверка текущего пароля — оракул.
 */
export type ChangeMyPasswordRequest = Wire<'scanner.admin.v1.ChangeMyPasswordRequest'>

/**
 * Текущая сессия ротируется (поэтому `tokens` надо положить в хранилище),
 * все остальные — гасятся; `revokedSessions` — сколько именно.
 */
export type ChangeMyPasswordResponse = WireWith<
  'scanner.admin.v1.ChangeMyPasswordResponse',
  { tokens: TokenPair }
>

/* ═════════════════════════════════════════════════════════════════════════
   4. СЕССИИ — свои (`/auth/sessions`) и чужие (`/users/{id}/sessions`)
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Ответ обоих списков сессий:
 *   `GET /api/v1/auth/sessions`        — свои, любая роль
 *   `GET /api/v1/users/{id}/sessions`  — чужие, только admin
 *
 * `total` — `uint32`, число. Пагинации у списка сессий нет: их единицы.
 * Оба эндпоинта возвращают одну и ту же форму, поэтому тип один.
 *
 * Псевдоним берётся из схемы `ListMySessionsResponse`; в контракте есть и
 * отдельная `ListUserSessionsResponse` с той же формой — структурно они
 * взаимозаменяемы, и расхождение формы всплывёт компиляцией на псевдониме ниже.
 */
export type ListSessionsResponse = WireWith<
  'scanner.admin.v1.ListMySessionsResponse',
  { sessions: Session[] }
>

/** @see ListSessionsResponse — `GET /api/v1/auth/sessions` */
export type ListMySessionsResponse = ListSessionsResponse

/** @see ListSessionsResponse — `GET /api/v1/users/{id}/sessions` */
export type ListUserSessionsResponse = WireWith<
  'scanner.admin.v1.ListUserSessionsResponse',
  { sessions: Session[] }
>

/**
 * `DELETE /api/v1/auth/sessions/{sessionId}` — любая роль, но запрос скоупится
 * по `user_id` принципала. Чужой `sessionId` даёт `SESSION_NOT_FOUND`, а не
 * `FORBIDDEN`: эндпоинт не подтверждает существование чужих сессий.
 *
 * Тела нет — `{}`.
 */
export type RevokeSessionResponse = Schemas['scanner.admin.v1.RevokeSessionResponse']

/**
 * `POST /api/v1/auth/sessions/revoke-others` — «выйти на всех устройствах».
 *
 * ⚠️ Тело пустое, но **отправлять его обязательно**: `axios.post(url)` без
 * второго аргумента не проставит `Content-Type` и получит `400 CODEC`.
 * Правильно: `http.post<RevokeOtherSessionsResponse>(url, {})`.
 */
export type RevokeOtherSessionsRequest = Schemas['scanner.admin.v1.RevokeOtherSessionsRequest']

/** `revoked` — сколько сессий погашено (текущая не в счёт). */
export type RevokeOtherSessionsResponse = Wire<'scanner.admin.v1.RevokeOtherSessionsResponse'>

/** `DELETE /api/v1/users/{id}/sessions` — admin гасит все сессии пользователя. */
export type RevokeUserSessionsResponse = Wire<'scanner.admin.v1.RevokeUserSessionsResponse'>

/* ═════════════════════════════════════════════════════════════════════════
   5. ПОЛЬЗОВАТЕЛИ — `UserService`, префикс `/api/v1/users`
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Допустимые значения `orderBy` в `GET /api/v1/users` — список закрыт на сервере,
 * всё остальное отвергается. Ведущий `-` — убывание, `""` — серверный дефолт.
 */
export const USER_ORDER_BY = [
  '',
  'created_at',
  '-created_at',
  'email',
  '-email',
  'username',
  '-username',
] as const
export type UserOrderBy = typeof USER_ORDER_BY[number]

/**
 * `GET /api/v1/users` — admin + viewer. Едет **query-строкой**, не телом.
 * Все поля опциональны на стороне клиента: не отправили — сервер взял дефолт.
 *
 * `status` и `role` симметричны: **невыставленный фильтр не фильтрует**.
 * Без `status` приходят и активные, и отключённые — в UI это три состояния
 * «Все» (по умолчанию) / «Активные» / «Отключённые». Проверено на живом
 * бэкенде: без фильтра `totalItems` включает отключённых.
 *
 * `role`/`status` со значением `*_UNSPECIFIED` эквивалентны отсутствию ключа.
 * `search` — подстрока по email / username / fullName.
 *
 * Берётся из `operations`, а не из `schemas`: у GET-запроса тела нет, и gnostic
 * описывает эти поля как query-параметры. `?` здесь **подлинный** — это ровно
 * то, что можно не отправлять, — поэтому `Required<>` не применяется.
 * `orderBy` сужен с `string` до закрытого union: см. `USER_ORDER_BY`.
 */
export type ListUsersRequest = Omit<
  NonNullable<operations['UserService_ListUsers']['parameters']['query']>,
  'orderBy'
> & {
  orderBy?: UserOrderBy
}

/**
 * ⚠️ `pageInfo` — **честный `PageInfo | null`.** Это message-поле, и если
 * сервер его не заполнил, protojson пришлёт ключ со значением `null`
 * (не `{}` и не отсутствие ключа). Вызывающий обязан защититься:
 *
 *     const total = resp.pageInfo?.totalItems ?? 0
 *
 * `users` при этом — всегда массив, `[]` в худшем случае.
 *
 * Рукописный: оба поля — вложенные сообщения, и от `Omit<Wire<...>, 'users'|'pageInfo'>`
 * остаётся `{}` (см. `LoginResponse`).
 *
 * ⚠️ Проверено на живом бэкенде (F4): сервис заполняет `pageInfo` **всегда**,
 * включая пустую страницу (`?page=99` → `{page:99, totalItems:2, totalPages:1}`)
 * и нулевую выборку (`?search=zzz` → `{page:1, totalItems:0, totalPages:0}`).
 * `| null` оставлен намеренно: это честная семантика protojson для
 * невыставленного message-поля, и стоит она одного `?.` в `stores/users.ts`.
 */
export interface ListUsersResponse {
  users: User[]
  pageInfo: PageInfo | null
}

/**
 * `POST /api/v1/users` — только admin.
 * `fullName` не `optional`: пустая строка — легальное «не заполнено».
 * `role` здесь обязателен и не должен быть `ROLE_UNSPECIFIED` — см. `ASSIGNABLE_ROLES`.
 * Пароль обязан пройти ту же политику, что и на сервере (8–128, строчная +
 * заглавная + цифра), иначе прилетит `WEAK_PASSWORD` с `metadata.rule`.
 */
export type CreateUserRequest = Wire<'scanner.admin.v1.CreateUserRequest'>

/** `POST /api/v1/users` — рукописный, чистая обёртка (см. `LoginResponse`). */
export interface CreateUserResponse {
  user: User
}

/** `GET /api/v1/users/{id}` — admin + viewer. Рукописный, чистая обёртка. */
export interface GetUserResponse {
  user: User
}

/**
 * `PATCH /api/v1/users/{id}` — только admin. **Частичное обновление через
 * proto3 `optional`**, не `FieldMask`. Отсюда три правила, и они не интуитивны:
 *
 * 1. **Ключ отсутствует → поле не меняется.** Именно поэтому все четыре поля
 *    здесь `T?`, а не `T | null` — в отличие от всего остального в этом файле.
 *    Формируйте тело только из тронутых полей (`dirty` во vee-validate),
 *    а не из всего объекта формы.
 *
 * 2. **`null` НЕ очищает поле.** `{"fullName": null}` protojson декодирует
 *    как *unset*, то есть ровно как отсутствие ключа: поле останется прежним.
 *    Обнулить что-либо `null`'ом по проводу невозможно.
 *
 * 3. **Чтобы записать пустое значение, шлите `""`.** `{"fullName": ""}` —
 *    выставленное поле с пустой строкой, оно и запишется.
 *
 * Роль здесь не меняется — для неё отдельный RPC (`PUT /users/{id}/role`),
 * у которого свой побочный эффект и своя строка в RBAC.
 *
 * `id` едет в пути, в теле его нет — но gnostic всё равно кладёт его в схему,
 * поэтому здесь он снимается `Omit`'ом. Единственный тип, к которому НЕ
 * применяется `Required<>`: здесь `?` подлинный (proto3 `optional`).
 */
export type UpdateUserRequest = Omit<Schemas['scanner.admin.v1.UpdateUserRequest'], 'id'>

/** `PATCH /api/v1/users/{id}` — рукописный, чистая обёртка. */
export interface UpdateUserResponse {
  user: User
}

/**
 * `PUT /api/v1/users/{id}/role` — только admin.
 * Смена роли гасит **все** сессии цели: старый access-токен несёт старую роль
 * в claim'ах и жил бы ещё до 15 минут.
 */
export type SetUserRoleRequest = WireBody<'scanner.admin.v1.SetUserRoleRequest'>

/** `revokedSessions` — сколько сессий цели погашено сменой роли. */
export type SetUserRoleResponse = WireWith<'scanner.admin.v1.SetUserRoleResponse', { user: User }>

/**
 * `POST /api/v1/users/{id}/password` — только admin, сброс чужого пароля.
 * Текущий пароль не требуется (в этом и разница с `ChangeMyPassword`).
 */
export type ResetUserPasswordRequest = WireBody<'scanner.admin.v1.ResetUserPasswordRequest'>

/** Сессии цели гасятся все; своих токенов админ при этом не теряет. */
export type ResetUserPasswordResponse = Wire<'scanner.admin.v1.ResetUserPasswordResponse'>

/**
 * `DELETE /api/v1/users/{id}` — только admin. Удаление **мягкое**:
 * пользователь получает `status: 'USER_STATUS_DISABLED'`, его сессии гасятся,
 * а в ответе приезжает обновлённая строка — её можно класть прямо в стор,
 * а не вычёркивать из списка.
 *
 * Инварианты `CANNOT_MODIFY_SELF` и `LAST_ADMIN` проверяются здесь же (409).
 */
export type DeleteUserResponse = WireWith<'scanner.admin.v1.DeleteUserResponse', { user: User }>

/* ═════════════════════════════════════════════════════════════════════════
   6. ПРАВА
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Плоский список прав из `GET /api/v1/auth/me`.
 *
 * • admin  — все шесть;
 * • viewer — `users.read` + `self.password.change`.
 *
 * ⚠️ Это **аффордансы UI, а не контроль доступа.** Авторитет — Go-бэкенд:
 * RBAC там fail-closed (неизвестная операция → 403), и viewer, вручную открывший
 * `/users/new`, получит 403 от сервера, даже если фронт кнопку показал.
 * Права нужны, чтобы не рисовать заведомо запрещённые действия, — не более.
 *
 * Сервер вправе прислать право, которого в этом списке нет: `GetMeResponse.permissions`
 * поэтому `string[]`, а union — для сравнений и словарей.
 */
export const PERMISSIONS = [
  'users.read',
  'users.write',
  'users.password.reset',
  'sessions.read.any',
  'sessions.revoke.any',
  'self.password.change',
] as const
export type Permission = typeof PERMISSIONS[number]

/* ═════════════════════════════════════════════════════════════════════════
   7. ОШИБКИ
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Конверт ошибки кратоса (`errors.Error`) — единая форма для **всех** не-2xx.
 *
 * • `code` — число, равное HTTP-статусу (кратос отдаёт `Error.Code` напрямую).
 * • `reason` — **ключ локализации**, по нему и только по нему ветвится фронт.
 * • `message` — текст для разработчика. **Никогда не показывать пользователю.**
 * • `metadata` — `map`, а значит присутствует всегда (`{}`, когда пусто):
 *   читается безусловно, `err.metadata.email` без `?.` не упадёт.
 *
 * `reason` типизирован `string`, а не `ErrorReason`, намеренно — см. ниже.
 * Нормализация в `ApiError{status, reason, metadata}` — задача `lib/api-error.ts`.
 */
export interface ErrorResponse {
  code: number
  reason: string
  message: string
  metadata: Record<string, string>
}

/**
 * Полный список `scanner.admin.v1.ErrorReason` — ровно 25 значений.
 * Это и есть словарь i18n-ключей фронта.
 *
 * HTTP-статусы (из аннотаций `errors.code` в `.proto`):
 *   400 — `VALIDATION_FAILED`, `WRONG_CURRENT_PASSWORD`, `WEAK_PASSWORD`,
 *         `INVALID_QR_PAYLOAD`, `IMPORT_FILE_INVALID`
 *   401 — `INVALID_CREDENTIALS`, `ACCESS_TOKEN_*`, `REFRESH_TOKEN_*`, `SESSION_REVOKED`
 *   403 — `FORBIDDEN`, `USER_DISABLED`
 *   404 — `SESSION_NOT_FOUND`, `USER_NOT_FOUND`, `PARTICIPANT_NOT_FOUND`
 *   409 — `EMAIL_ALREADY_EXISTS`, `USERNAME_ALREADY_EXISTS`, `CANNOT_MODIFY_SELF`, `LAST_ADMIN`
 *   413 — `IMPORT_FILE_TOO_LARGE`
 *   429 — `RATE_LIMITED`
 *   500 — `UNKNOWN` (default_code)
 *
 * Четыре последних значения пришли с `ParticipantService` (§8). Два из них
 * рождаются на эндпоинтах, которых нет в `openapi.yaml` (импорт файла), —
 * это ещё одна причина, по которой список рукописный, а не выведенный.
 */
export const ERROR_REASONS = [
  'UNKNOWN',
  'VALIDATION_FAILED',
  'INVALID_CREDENTIALS',
  'ACCESS_TOKEN_MISSING',
  'ACCESS_TOKEN_INVALID',
  'ACCESS_TOKEN_EXPIRED',
  'REFRESH_TOKEN_INVALID',
  'REFRESH_TOKEN_EXPIRED',
  'REFRESH_TOKEN_REUSED',
  'SESSION_REVOKED',
  'SESSION_NOT_FOUND',
  'FORBIDDEN',
  'USER_NOT_FOUND',
  'EMAIL_ALREADY_EXISTS',
  'USERNAME_ALREADY_EXISTS',
  'USER_DISABLED',
  'WRONG_CURRENT_PASSWORD',
  'WEAK_PASSWORD',
  'CANNOT_MODIFY_SELF',
  'LAST_ADMIN',
  'RATE_LIMITED',
  'PARTICIPANT_NOT_FOUND',
  'INVALID_QR_PAYLOAD',
  'IMPORT_FILE_INVALID',
  'IMPORT_FILE_TOO_LARGE',
] as const
export type ErrorReason = typeof ERROR_REASONS[number]

/**
 * ⚠️ **`reason` с провода может НЕ входить в `ErrorReason`.**
 *
 * Три источника ошибок лежат в кратосе выше нашего кода и приносят свои
 * собственные `reason`, которых в нашем enum нет:
 *
 *   `VALIDATOR`    — middleware protoc-gen-validate: форма запроса не прошла
 *                    PGV-правила, до `biz` дело не дошло (400).
 *   `CODEC`        — тело не разобралось: битый JSON или **отсутствующий
 *                    `Content-Type`** (400). См. мину в шапке файла.
 *   `UNAUTHORIZED` — встроенный `middleware/auth/jwt` (401).
 *
 * Бэкенд планирует нормализовать их (`auth.NormalizeJWTErrors()` и разбор PGV),
 * но фронт **обязан быть защитным**: он старше или новее бэкенда ровно на один
 * деплой. Поэтому `ErrorResponse.reason` — `string`, а не union; неизвестный
 * ключ должен давать общий текст, а не падать и не показывать `message`.
 *
 * Эта константа существует, чтобы `lib/api-error.ts` мог дать им человеческие
 * тексты, **не расширяя** `ErrorReason` (там ровно 21 значение контракта).
 */
export const UNNORMALIZED_ERROR_REASONS = ['VALIDATOR', 'CODEC', 'UNAUTHORIZED'] as const
export type UnnormalizedErrorReason = typeof UNNORMALIZED_ERROR_REASONS[number]

/**
 * Полезная нагрузка `metadata` по `reason` — сервер кладёт туда ровно то,
 * что нужно, чтобы не разбирать `message` текстом:
 *
 * | `reason`                  | ключ                   | что внутри |
 * |---------------------------|------------------------|------------|
 * | `EMAIL_ALREADY_EXISTS`    | `metadata.email`       | занятый email — прямо в `setErrors({ email })` |
 * | `USERNAME_ALREADY_EXISTS` | `metadata.username`    | занятый username |
 * | `WEAK_PASSWORD`           | `metadata.rule`        | какое правило нарушено — см. `WEAK_PASSWORD_RULES` |
 * | `RATE_LIMITED`            | `metadata.retry_after_s` | секунды до следующей попытки, **строкой** — `Number(...)` |
 * | `VALIDATION_FAILED`       | `metadata.field`       | имя поля в snake_case, как в `.proto` |
 *
 * Ключи `metadata` — snake_case (это обычный `map<string,string>`, а не поля
 * сообщения, camelCase-конвертация protojson на них не распространяется).
 * Значения — всегда строки, даже числовые.
 */
export const WEAK_PASSWORD_RULES = [
  'min_length',
  'max_length',
  'needs_lower',
  'needs_upper',
  'needs_digit',
  'contains_login',
  'control_chars',
] as const
export type WeakPasswordRule = typeof WEAK_PASSWORD_RULES[number]

/* ═════════════════════════════════════════════════════════════════════════
   8. УЧАСТНИКИ — `ParticipantService`, префикс `/api/v1/participants`
   ═════════════════════════════════════════════════════════════════════════

   Раздел стоит последним, а не рядом с пользователями, по механической
   причине: номера §1–§7 уже процитированы из `lib/`, `composables/` и вью,
   и сдвиг нумерации превратил бы каждую такую ссылку в ложную. Содержательно
   он самодостаточен — `Participant` не встречается ни в одном ответе других
   сервисов, поэтому enum, сообщение и запросы живут здесь вместе.

   ⚠️ ДВА ЭНДПОИНТА РАЗДЕЛА В `openapi.yaml` ОТСУТСТВУЮТ ФИЗИЧЕСКИ.
   У `ImportParticipants` и `DownloadImportTemplate` нет аннотации
   `google.api.http`: они возят не JSON, а файлы (multipart на вход, xlsx/csv
   на выход), и сгенерированный роут принимал бы файл base64-строкой внутри
   JSON. HTTP для них написан на бэкенде руками, поэтому gnostic их не видит,
   а `api.gen.ts` о них не знает. Их типы ниже — рукописные, источник истины
   `api/admin/v1/participant.proto`. */

/**
 * Значения enum'а `scanner.admin.v1.ParticipantStatus` ровно как они едут
 * по проводу. Union берётся из контракта, список `PARTICIPANT_STATUSES`
 * остаётся рукописным — итерируемого рантайм-значения из типа не сделать.
 * @see Role
 */
export type ParticipantStatus = NonNullable<Schemas['scanner.admin.v1.Participant']['status']>
export const PARTICIPANT_STATUSES = [
  'PARTICIPANT_STATUS_UNSPECIFIED',
  'PARTICIPANT_STATUS_PARTICIPATING',
  'PARTICIPANT_STATUS_NOT_PARTICIPATING',
] as const satisfies readonly ParticipantStatus[]

/**
 * Статусы, которые реально можно выставить: `PARTICIPANT_STATUS_UNSPECIFIED`
 * — протобуфный ноль, а не выбор человека. `SetParticipantStatus` его и не
 * примет (`not_in: [0]` в PGV-правилах), а `CreateParticipant` трактует как
 * «по умолчанию, то есть участвует».
 */
export const ASSIGNABLE_PARTICIPANT_STATUSES = [
  'PARTICIPANT_STATUS_PARTICIPATING',
  'PARTICIPANT_STATUS_NOT_PARTICIPATING',
] as const satisfies readonly ParticipantStatus[]

/**
 * Русские подписи статусов участника — **единственное** место, где они живут.
 * Карта тотальная (`Record<ParticipantStatus, string>`), поэтому индексация
 * всегда даёт строку и `?? fallback` по вьюхам не расползается.
 * См. комментарий к `ROLE_LABELS`.
 */
export const PARTICIPANT_STATUS_LABELS: Record<ParticipantStatus, string> = {
  PARTICIPANT_STATUS_UNSPECIFIED: 'Не указан',
  PARTICIPANT_STATUS_PARTICIPATING: 'Участвует',
  PARTICIPANT_STATUS_NOT_PARTICIPATING: 'Не участвует',
}

/**
 * `scanner.admin.v1.Participant`.
 *
 * • ФИО разложено на три поля: `lastName` и `firstName` непустые,
 *   `middleName` — **скаляр**, то есть `''` у тех, у кого отчества нет
 *   (не `null` и не отсутствие ключа).
 * • Три булевых поля — справка / взнос / весовой контроль. Скаляры, `false`
 *   по умолчанию.
 * • `qrPayload` — `base64url(16 сырых байт UUID)` без паддинга, ровно
 *   22 символа. **Вычисляем из `id`** и отдельной колонкой не хранится,
 *   так что рассинхронизировать его с участником нечем.
 * • `createdAt` / `updatedAt` сервер выставляет всегда.
 *
 * Ни одного nullable-поля здесь нет: все поля — скаляры и enum'ы, а `Timestamp`
 * заполнен всегда. Поэтому `Wire<>` без перекрытий (ср. `User.lastLoginAt`).
 */
export type Participant = Wire<'scanner.admin.v1.Participant'>

/**
 * Допустимые значения `orderBy` в `GET /api/v1/participants` — список закрыт
 * на сервере (`in: [...]` в PGV), всё остальное отвергается.
 *
 * ⚠️ Дефолт здесь **не** `-created_at`, как у пользователей: пустая строка
 * означает `last_name` по возрастанию. Людей в списке ищут глазами по алфавиту,
 * а не по дате появления в базе.
 */
export const PARTICIPANT_ORDER_BY = [
  '',
  'last_name',
  '-last_name',
  'created_at',
  '-created_at',
] as const
export type ParticipantOrderBy = typeof PARTICIPANT_ORDER_BY[number]

/**
 * `GET /api/v1/participants` — admin + viewer. Едет **query-строкой**, не телом.
 *
 * `status` симметричен пользовательскому: невыставленный фильтр не фильтрует,
 * `PARTICIPANT_STATUS_UNSPECIFIED` эквивалентен отсутствию ключа.
 *
 * ⚠️ `medicalCertificate` / `feePaid` / `weighInPassed` — **трёхпозиционные**:
 * это proto3 `optional bool`, и «не важно» кодируется **отсутствием ключа**,
 * а не `false`. `?medicalCertificate=false` означает «только те, у кого справки
 * нет», и спутать это с «не фильтруем» — самый дорогой баг этого экрана.
 * Отсюда `boolean | null` в сторе и три состояния в каждом селекте.
 *
 * `search` — подстрока по фамилии, имени и отчеству.
 *
 * Берётся из `operations`, а не из `schemas`: у GET-запроса тела нет. `?` здесь
 * **подлинный**, поэтому `Required<>` не применяется. `orderBy` сужен со
 * `string` до закрытого union — см. `PARTICIPANT_ORDER_BY`.
 */
export type ListParticipantsRequest = Omit<
  NonNullable<operations['ParticipantService_ListParticipants']['parameters']['query']>,
  'orderBy'
> & {
  orderBy?: ParticipantOrderBy
}

/**
 * ⚠️ `pageInfo` — честный `PageInfo | null` ровно по тем же причинам, что и в
 * `ListUsersResponse`: это message-поле, и «не выставлено» protojson кодирует
 * ключом со значением `null`. Читать только через `?.`.
 *
 * `participants` при этом — всегда массив, `[]` в худшем случае.
 * Рукописный: оба поля вложенные, от `Omit<Wire<...>, 'participants'|'pageInfo'>`
 * остаётся `{}` (см. `LoginResponse`).
 */
export interface ListParticipantsResponse {
  participants: Participant[]
  pageInfo: PageInfo | null
}

/** `GET /api/v1/participants/{id}` — admin + viewer. Рукописный, чистая обёртка. */
export interface GetParticipantResponse {
  participant: Participant
}

/**
 * `POST /api/v1/participants` — только admin.
 *
 * `middleName` не `optional`: пустая строка — легальное «отчества нет».
 * `status` обязателен на уровне типа, но `PARTICIPANT_STATUS_UNSPECIFIED`
 * сервер трактует как «участвует» (см. `ASSIGNABLE_PARTICIPANT_STATUSES`).
 * `qrPayload` не передаётся: он выводится из сгенерированного сервером UUID,
 * поэтому QR у участника есть с первой секунды существования.
 */
export type CreateParticipantRequest = Wire<'scanner.admin.v1.CreateParticipantRequest'>

/** `POST /api/v1/participants` — рукописный, чистая обёртка. */
export interface CreateParticipantResponse {
  participant: Participant
}

/**
 * `PATCH /api/v1/participants/{id}` — только admin. **Частичное обновление
 * через proto3 `optional`**, не `FieldMask`, поэтому правила ровно те же, что
 * у `UpdateUserRequest`, и они не интуитивны:
 *
 * 1. **Ключ отсутствует → поле не меняется.** Отсюда `T?` у всех шести полей.
 *    Тело собирается из тронутых полей формы, а не из всего объекта.
 * 2. **`null` НЕ очищает поле** — protojson декодирует его как *unset*.
 * 3. **Пустое значение пишется пустой строкой:** `{"middleName": ""}` —
 *    это и есть «отчества нет».
 *
 * Статус здесь не меняется — для него отдельный RPC (`PUT .../status`):
 * «отметить участие» вызывают со сканера, и оно не должно уметь заодно
 * переписать ФИО.
 *
 * `id` едет в пути; gnostic всё равно кладёт его в схему, поэтому `Omit`.
 * `Required<>` не применяется: здесь `?` подлинный.
 */
export type UpdateParticipantRequest = Omit<Schemas['scanner.admin.v1.UpdateParticipantRequest'], 'id'>

/** `PATCH /api/v1/participants/{id}` — рукописный, чистая обёртка. */
export interface UpdateParticipantResponse {
  participant: Participant
}

/**
 * `PUT /api/v1/participants/{id}/status` — только admin.
 * `PARTICIPANT_STATUS_UNSPECIFIED` отвергается валидатором (`not_in: [0]`):
 * «снять с участия» — это `PARTICIPANT_STATUS_NOT_PARTICIPATING`, а не ноль.
 */
export type SetParticipantStatusRequest = WireBody<'scanner.admin.v1.SetParticipantStatusRequest'>

/** `PUT /api/v1/participants/{id}/status` — рукописный, чистая обёртка. */
export interface SetParticipantStatusResponse {
  participant: Participant
}

/**
 * `DELETE /api/v1/participants/{id}` — только admin.
 *
 * ⚠️ Удаление **жёсткое**, в отличие от пользователей: у участника нет ни
 * сессий, ни истории, а ошибочная строка из импорта должна убираться насовсем.
 * Поэтому в интерфейсе здесь честное «Удалить», а «снять с соревнований» —
 * это смена статуса, а не удаление.
 *
 * В ответе приезжает **удалённый** участник — чтобы показать «удалён Иванов И.И.»,
 * не держа копию строки заранее.
 */
export interface DeleteParticipantResponse {
  participant: Participant
}

/**
 * `POST /api/v1/participants/resolve-qr` — admin + viewer.
 *
 * POST, а не GET с payload'ом в пути: алфавит base64url безопасен для URL, но
 * идентификатор человека в строке запроса оседает в логах всех прокси на пути.
 *
 * `payload` — ровно то, что декодировал сканер. Мусор даёт `INVALID_QR_PAYLOAD`
 * (400), корректный код несуществующего участника — `PARTICIPANT_NOT_FOUND` (404).
 */
export type ResolveParticipantQrRequest = Wire<'scanner.admin.v1.ResolveParticipantQrRequest'>

/** `POST /api/v1/participants/resolve-qr` — рукописный, чистая обёртка. */
export interface ResolveParticipantQrResponse {
  participant: Participant
}

/* ─────────────────────────────────────────────────────────────────────────
   8.1 ИМПОРТ — ДВЕ ФАЗЫ, ПОТОМУ ЧТО УЧАСТНИК НИЧЕМ НЕ УНИКАЛЕН
   ─────────────────────────────────────────────────────────────────────────

   У участника нет ни одного уникального поля: ни почты, ни номера, ни
   отдельного идентификатора в файле. Полные тёзки в списке на несколько сотен
   человек — норма, а не аномалия. Поэтому сервер **никогда не сопоставляет
   строку файла с существующей записью сам**: угадав не того, он переписал бы
   допуски живому участнику, и узнали бы об этом на регистрации.

   Отсюда две фазы, и границу между ними держит контракт, а не дисциплина
   вызывающего кода:

     ФАЗА 1 — `POST /api/v1/participants/import` (multipart, поле `file`).
       Разбирает файл и **ничего не пишет**. Возвращает все строки в исходном
       порядке (`ImportRow`), а к каждой — список существующих участников с тем
       же ФИО (`matches`). Это подсказка, а не решение: список бывает пустым и
       бывает длиннее одного.

     ФАЗА 2 — `POST /api/v1/participants/import/apply` (обычный JSON).
       Единственный путь, которым импорт пишет в базу. Файл сюда не едет:
       строки уже разобраны, и повторный разбор применил бы решения оператора
       к другим байтам, если он успел поправить файл на диске.

   ⚠️ Флага `dryRun` больше нет. «Посмотреть, ничего не записав» — это фаза 1
   целиком, а не режим одного эндпоинта; булев переключатель, от которого
   зависело, пишет запрос в базу или нет, здесь просто нечему заменять.

   ⚠️ В `openapi.yaml` из трёх эндпоинтов раздела есть только `…/import/apply`.
   `ImportParticipants` и `DownloadImportTemplate` возят файлы, у их RPC нет
   аннотации `google.api.http`, и gnostic их не видит — их типы ниже рукописные,
   источник истины `api/admin/v1/participant.proto`. Типы фазы 2 выведены из
   контракта. */

/** Форматы шаблона `GET /api/v1/participants/import/template?format=…`. */
export const IMPORT_TEMPLATE_FORMATS = ['xlsx', 'csv'] as const
export type ImportTemplateFormat = typeof IMPORT_TEMPLATE_FORMATS[number]

/**
 * Лимит размера файла импорта, байты (5 MiB) — продублирован с сервера
 * (`max_len` в PGV и ручная проверка в роуте) осознанно: это подсказка
 * пользователю **до** загрузки, а не источник правды. Превышение всё равно
 * вернётся как `IMPORT_FILE_TOO_LARGE` (413).
 */
export const IMPORT_MAX_FILE_BYTES = 5_242_880

/**
 * Потолок решений в одном применении — `max_items: 2000` на
 * `ApplyParticipantImportRequest.decisions`. Тоже копия серверной константы и
 * тоже ради подсказки до запроса: превышение вернётся как `VALIDATION_FAILED`
 * на весь запрос целиком, то есть импорт не пройдёт ни для одной строки.
 */
export const IMPORT_MAX_DECISIONS = 2000

/**
 * `scanner.admin.v1.ParticipantInput` — значения участника, приехавшие из файла.
 *
 * Одно сообщение на обе стороны: назад в превью (`ImportRow.data`) и вперёд
 * в решение (`ImportDecision.data`). Два зеркальных типа разъехались бы, и
 * импорт показывал бы одно, а записывал другое.
 *
 * ⚠️ ГЛАВНОЕ ПРО ЭТОТ ТИП — ТРИ `?` НА ФЛАГАХ И `?` НА СТАТУСЕ.
 * Это proto3 `optional`, и `?` здесь **подлинный** (см. п. 3 «Формат провода»
 * в шапке файла), а значит несёт смысл, которого нет больше нигде в контракте:
 *
 *   ключа нет   → такой **колонки в файле нет**. При обновлении поле остаётся
 *                 как было: файл федерации без колонки «Взнос» не имеет права
 *                 обнулить оплату всему списку;
 *   `false`     → колонка есть, ячейка пустая или «нет». Это осознанное «нет»,
 *                 и при обновлении оно снимет галочку.
 *
 * Спутать эти два состояния в интерфейсе — самая дорогая ошибка этого экрана,
 * поэтому «колонки нет» показывается отдельным знаком, а не как «нет».
 *
 * ФИО, наоборот, обязательно: `lastName`/`firstName` непустые (PGV `min_len: 1`),
 * `middleName` — скаляр, `''` у тех, у кого отчества нет.
 */
type ParticipantInputSchema = Schemas['scanner.admin.v1.ParticipantInput']
export type ParticipantInput =
  & Required<Pick<ParticipantInputSchema, 'lastName' | 'firstName' | 'middleName'>>
  & Pick<ParticipantInputSchema, 'medicalCertificate' | 'feePaid' | 'weighInPassed' | 'status'>

/**
 * Одна строка файла в ответе фазы 1 — **и годная, и отвергнутая**.
 *
 * Рукописный: `ImportParticipants` не попал в `openapi.yaml` (см. шапку раздела).
 *
 * • `row` — номер строки **как его видит человек в Excel**, 1-based; строка 1
 *   это заголовок, поэтому первая строка данных — 2.
 * • `raw` — исходная строка целиком, чтобы человек нашёл её в своём файле.
 * • `data` — разобранные значения или **`null`**, если строка не разобралась.
 *   `null`, а не отсутствие ключа: это message-поле, protojson печатает его
 *   ключом со значением `null` (п. 2 «Формат провода»). Такую строку нельзя
 *   ни создать, ни обновить — только пропустить.
 * • `errorReason` — имя значения `ErrorReason`, ключ локализации. Пусто, если
 *   строка годна. Типизирован `string`, а не `ErrorReason`, по той же причине,
 *   что и в `ErrorResponse`: сервер вправе прислать причину, о которой этот
 *   билд фронта не знает.
 * • `errorMessage` — пояснение **оператору и по-русски**: «колонка «Имя» не
 *   заполнена». В отличие от `message` в конверте ошибки, это текст для
 *   человека, и показывать его можно (см. `components/participants/import-reason.ts`).
 * • `matches` — существующие участники с тем же ФИО. **Подсказка, а не решение**:
 *   сервер никого не выбирает, в списке может быть несколько тёзок и может не
 *   быть никого.
 */
export interface ImportRow {
  row: number
  raw: string
  data: ParticipantInput | null
  errorReason: string
  errorMessage: string
  matches: Participant[]
}

/**
 * Ответ фазы 1 — `POST /api/v1/participants/import`. Рукописный.
 *
 * Счётчиков здесь нет и быть не может: пока оператор не принял решения, считать
 * нечего. `totalRows` — строк данных в файле (заголовок не считается), и он
 * обязан совпадать с `rows.length`; расхождение означает, что сервер что-то
 * потерял по дороге, и это повод показать предупреждение, а не молча доверять.
 */
export interface ImportParticipantsResponse {
  totalRows: number
  rows: ImportRow[]
}

/**
 * Значения enum'а `scanner.admin.v1.ImportAction` ровно как они едут по проводу.
 * @see Role
 */
export type ImportAction = NonNullable<Schemas['scanner.admin.v1.ImportDecision']['action']>
export const IMPORT_ACTIONS = [
  'IMPORT_ACTION_UNSPECIFIED',
  'IMPORT_ACTION_CREATE',
  'IMPORT_ACTION_UPDATE',
  'IMPORT_ACTION_SKIP',
] as const satisfies readonly ImportAction[]

/**
 * Действия, которые оператор реально может выбрать. `IMPORT_ACTION_UNSPECIFIED`
 * — протобуфный ноль, и сервер его отвергает (`not_in: [0]` в PGV-правилах),
 * то есть «решение не принято» по проводу непредставимо. Из этого следует
 * важное для интерфейса: нерешённую строку нельзя «отправить как есть» —
 * её надо либо решить, либо не отправлять.
 */
export const DECIDABLE_IMPORT_ACTIONS = [
  'IMPORT_ACTION_CREATE',
  'IMPORT_ACTION_UPDATE',
  'IMPORT_ACTION_SKIP',
] as const satisfies readonly ImportAction[]
export type DecidableImportAction = typeof DECIDABLE_IMPORT_ACTIONS[number]

/**
 * Русские подписи действий — **единственное** место, где они живут.
 * Карта тотальная (`Record<DecidableImportAction, string>`), поэтому новое
 * значение в `.proto` ломает компиляцию, а не рантайм. См. `ROLE_LABELS`.
 */
export const IMPORT_ACTION_LABELS: Record<DecidableImportAction, string> = {
  IMPORT_ACTION_CREATE: 'Создать нового',
  IMPORT_ACTION_UPDATE: 'Обновить существующего',
  IMPORT_ACTION_SKIP: 'Пропустить',
}

/**
 * Решение оператора по одной строке файла.
 *
 * Рукописный, хотя схема в контракте есть: у сгенерированной `?` стоит на
 * **каждом** поле, включая `action`, — а решение без действия отвергается
 * сервером. Здесь `row` и `action` обязательны, а `?` оставлены ровно там, где
 * они значимы:
 *
 * • `targetId` — **обязателен при `IMPORT_ACTION_UPDATE` и запрещён при
 *   остальных**. Условное правило PGV выразить не умеет, проверка стоит в biz,
 *   поэтому «обновить, не сказав кого» компилируется и падает уже на сервере —
 *   ловить это обязан интерфейс.
 * • `data` — значения из файла. При `IMPORT_ACTION_SKIP` игнорируются, поэтому
 *   строку, которая не разобралась (`ImportRow.data === null`), можно честно
 *   отправить пропуском и не терять её из арифметики отчёта.
 */
export interface ImportDecision {
  row: number
  action: DecidableImportAction
  targetId?: string
  data?: ParticipantInput
}

/**
 * Запрос фазы 2 — `POST /api/v1/participants/import/apply`.
 *
 * Рукописный по той же причине, что и `ImportDecision`: `Wire<>` восстановил бы
 * обязательность только на верхнем уровне, а элементы массива остались бы сырой
 * схемой со всеми полями `?`.
 *
 * `min_items: 1` — пустой список сервер отвергает: «применить ничего» это не
 * операция, а недосмотр интерфейса.
 */
export interface ApplyParticipantImportRequest {
  decisions: ImportDecision[]
}

/**
 * Одна отвергнутая строка.
 *
 * • `row` — номер строки как в Excel, 1-based (см. `ImportRow.row`).
 * • `reason` — имя значения `ErrorReason`, ключ локализации. `string`, а не
 *   `ErrorReason`: сервер вправе прислать причину, о которой этот билд не знает.
 * • `raw` — исходная строка целиком, чтобы человек нашёл её в своём файле.
 */
export type ImportRowError = Wire<'scanner.admin.v1.ImportRowError'>

/**
 * Ответ фазы 2 — единственный ответ импорта, за которым стоит запись в базу.
 *
 * • `created` / `updated` / `skipped` / `failed` — по одному счётчику на исход,
 *   в сумме дают число отправленных решений.
 * • `errors` — **только отвергнутые** строки. Успешные не перечисляются: на
 *   списке в тысячу человек ответ иначе весит больше самого файла.
 *
 * ⚠️ `created + updated > 0 && failed > 0` — **частичный успех**, самостоятельный
 * исход, а не «почти успех» и не «почти провал»: часть людей уже в базе, и
 * повторять импорт целиком нельзя.
 */
export type ApplyParticipantImportResponse = WireWith<
  'scanner.admin.v1.ApplyParticipantImportResponse',
  { errors: ImportRowError[] }
>
